import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, type LogEntry } from "@/lib/api";
import { getAccessToken, tryRestoreSession } from "@/lib/api";
import { ArrowLeft, Copy, Check, ChevronDown, Loader2 } from "lucide-react";

const WS_BASE = import.meta.env.VITE_WS_URL ?? "";

type LogSource = "do_deploy" | "do_runtime" | "cf_build" | "fly";

const SOURCES: { id: LogSource; label: string; shortLabel: string }[] = [
  { id: "do_deploy", label: "DO Deploy", shortLabel: "Deploy" },
  { id: "do_runtime", label: "DO Runtime", shortLabel: "Runtime" },
  { id: "cf_build", label: "CF Build", shortLabel: "CF" },
  { id: "fly", label: "Fly Agent", shortLabel: "Fly" },
];

const LEVEL_COLORS: Record<string, string> = {
  error: "text-red-400",
  warn: "text-amber-400",
  debug: "text-daydra-muted/60",
  info: "text-daydra-dim",
};

function formatTimestamp(ts: string): string {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return ts.slice(11, 19) || "";
  }
}

export default function Logs() {
  const navigate = useNavigate();
  const [activeSource, setActiveSource] = useState<LogSource>("do_deploy");
  const [entries, setEntries] = useState<Record<LogSource, LogEntry[]>>({
    do_deploy: [],
    do_runtime: [],
    cf_build: [],
    fly: [],
  });
  const [loading, setLoading] = useState<Record<LogSource, boolean>>({
    do_deploy: false,
    do_runtime: false,
    cf_build: false,
    fly: false,
  });
  const [errors, setErrors] = useState<Record<LogSource, string | null>>({
    do_deploy: null,
    do_runtime: null,
    cf_build: null,
    fly: null,
  });
  const [copied, setCopied] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);

  const logContainerRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-scroll when new entries arrive
  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [entries, activeSource, autoScroll]);

  // Detect manual scroll to disable auto-scroll
  const handleScroll = useCallback(() => {
    const el = logContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setAutoScroll(atBottom);
  }, []);

  // WebSocket connection for real-time log streaming
  const connectWs = useCallback(() => {
    const token = getAccessToken();
    if (!token) return;

    const base = WS_BASE || window.location.origin;
    const wsUrl = base.replace(/^http/, "ws");
    const ws = new WebSocket(`${wsUrl}/ws/concierge`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        type: "auth",
        token,
        client_hour: new Date().getHours(),
      }));

      // Subscribe to all log sources
      setTimeout(() => {
        ws.send(JSON.stringify({
          type: "logs_subscribe",
          sources: SOURCES.map((s) => s.id),
        }));
      }, 500);
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "log_chunk" && msg.source && msg.entries) {
          setEntries((prev) => ({
            ...prev,
            [msg.source]: [...(prev[msg.source as LogSource] || []), ...msg.entries],
          }));
        }
      } catch {
        // ignore
      }
    };

    ws.onclose = (event) => {
      wsRef.current = null;
      if (event.code === 4003 || event.code === 4001) {
        tryRestoreSession().then((restored) => {
          if (restored) {
            reconnectTimer.current = setTimeout(connectWs, 500);
          }
        });
        return;
      }
      // Auto-reconnect
      reconnectTimer.current = setTimeout(connectWs, 3000);
    };

    ws.onerror = () => {};
  }, []);

  // Initial data fetch + WS connect
  useEffect(() => {
    // Fetch initial logs for all sources in parallel
    async function fetchInitial() {
      const fetchers: [LogSource, () => Promise<{ entries: LogEntry[] }>][] = [
        ["do_deploy", () => api.logs.doDeployLogs()],
        ["do_runtime", () => api.logs.doRuntimeLogs()],
        ["cf_build", () => api.logs.cfBuildLogs()],
        ["fly", () => api.logs.flyLogs()],
      ];

      for (const [source, fetcher] of fetchers) {
        setLoading((prev) => ({ ...prev, [source]: true }));
        try {
          const result = await fetcher();
          setEntries((prev) => ({ ...prev, [source]: result.entries || [] }));
          setErrors((prev) => ({ ...prev, [source]: null }));
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          setErrors((prev) => ({ ...prev, [source]: msg }));
        }
        setLoading((prev) => ({ ...prev, [source]: false }));
      }
    }

    fetchInitial();

    connectWs();

    return () => {
      // Unsubscribe and disconnect
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "logs_unsubscribe" }));
      }
      wsRef.current?.close();
      wsRef.current = null;
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
    };
  }, [connectWs]);

  // Copy all logs for active source
  async function handleCopyAll() {
    const sourceEntries = entries[activeSource];
    if (!sourceEntries.length) return;

    const text = sourceEntries
      .map((e) => `${formatTimestamp(e.timestamp)} [${e.level}] ${e.message}`)
      .join("\n");

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  const activeEntries = entries[activeSource];
  const activeError = errors[activeSource];
  const isLoading = loading[activeSource];

  return (
    <div className="flex h-full flex-col bg-daydra-bg">
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-daydra-border safe-top shrink-0">
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 rounded-lg hover:bg-daydra-surface/60 transition-colors"
          title="Back"
        >
          <ArrowLeft size={18} className="text-daydra-muted" />
        </button>
        <h1 className="text-sm font-medium text-daydra-text">Logs</h1>
        <div className="flex-1" />
        <span className="text-2xs text-daydra-muted">
          {activeEntries.length} {activeEntries.length === 1 ? "line" : "lines"}
        </span>
      </header>

      {/* Tab bar */}
      <div className="flex border-b border-daydra-border shrink-0 overflow-x-auto">
        {SOURCES.map((source) => (
          <button
            key={source.id}
            onClick={() => setActiveSource(source.id)}
            className={`relative px-4 py-2.5 text-xs font-medium whitespace-nowrap transition-colors ${
              activeSource === source.id
                ? "text-daydra-accent"
                : "text-daydra-muted hover:text-daydra-dim"
            }`}
          >
            <span className="hidden sm:inline">{source.label}</span>
            <span className="sm:hidden">{source.shortLabel}</span>
            {entries[source.id].length > 0 && (
              <span className={`ml-1.5 text-2xs ${
                activeSource === source.id ? "text-daydra-accent/70" : "text-daydra-muted/60"
              }`}>
                {entries[source.id].length}
              </span>
            )}
            {activeSource === source.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-daydra-accent" />
            )}
          </button>
        ))}
      </div>

      {/* Log viewer */}
      <div
        ref={logContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto overflow-x-auto font-mono text-xs leading-relaxed"
      >
        {isLoading && activeEntries.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 size={16} className="text-daydra-muted animate-spin" />
          </div>
        ) : activeError ? (
          <div className="flex flex-col items-center justify-center h-32 gap-1 px-4">
            <span className="text-red-400 text-xs">Failed to fetch</span>
            <span className="text-daydra-muted text-2xs text-center break-all">{activeError}</span>
          </div>
        ) : activeEntries.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-daydra-muted text-xs">
            No logs available for this source
          </div>
        ) : (
          <div className="p-3 space-y-0">
            {activeEntries.map((entry, i) => (
              <div
                key={`${entry.timestamp}-${i}`}
                className="flex gap-2 py-0.5 hover:bg-daydra-surface/30 px-1 rounded"
              >
                <span className="text-daydra-muted/40 shrink-0 hidden sm:inline select-none w-16 text-right">
                  {formatTimestamp(entry.timestamp)}
                </span>
                <span className={`${LEVEL_COLORS[entry.level] || "text-daydra-dim"} whitespace-pre-wrap break-all`}>
                  {entry.message}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom bar — copy + auto-scroll */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-t border-daydra-border shrink-0 safe-bottom">
        <button
          onClick={handleCopyAll}
          disabled={activeEntries.length === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-daydra-surface/60 text-daydra-dim hover:text-daydra-text hover:bg-daydra-surface transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {copied ? (
            <>
              <Check size={13} />
              Copied
            </>
          ) : (
            <>
              <Copy size={13} />
              Copy All
            </>
          )}
        </button>
        <div className="flex-1" />
        {!autoScroll && (
          <button
            onClick={() => {
              setAutoScroll(true);
              logContainerRef.current?.scrollTo({
                top: logContainerRef.current.scrollHeight,
                behavior: "smooth",
              });
            }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-daydra-muted hover:text-daydra-accent bg-daydra-surface/60 hover:bg-daydra-surface transition-colors"
          >
            <ChevronDown size={13} />
            <span className="hidden sm:inline">Follow</span>
          </button>
        )}
      </div>
    </div>
  );
}
